import type { DefectStage } from '@felix/domain';
import type { DefectSummary } from './types.js';

/** Maintenance (PMS) ▸ Defects (ADR 0060 §11). */
export const DEFECTS_PATH = '/pms/defects';

export type DefectsRoute =
  | { view: 'list' }
  | {
      view: 'new';
      equipmentId: string | null;
      dailyReportId: string | null;
      /** HSEQ raporundan (`&hse=…`; ADR 0096 §5). */
      hseReportId?: string | null;
      /** PMS bakımı sırasında, iş emrinden (`&wo=…`; ADR 0096 §6). */
      workOrderId?: string | null;
    }
  | { view: 'detail'; id: string };

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const uuid = (value: string | null) => (value && UUID.test(value) ? value : null);

/** Görünüm sorgu dizesinden: `?id=…`, `?new=1&equipment=…&daily=…&hse=…&wo=…`. */
export function defectsRoute(search: string): DefectsRoute {
  const params = new URLSearchParams(search);
  const id = uuid(params.get('id'));
  if (id) return { view: 'detail', id };
  if (params.get('new') === '1') {
    return {
      view: 'new',
      equipmentId: uuid(params.get('equipment')),
      dailyReportId: uuid(params.get('daily')),
      hseReportId: uuid(params.get('hse')),
      workOrderId: uuid(params.get('wo')),
    };
  }
  return { view: 'list' };
}

export function defectsPath(route: DefectsRoute): string {
  switch (route.view) {
    case 'list':
      return DEFECTS_PATH;
    case 'new': {
      const params = new URLSearchParams({ new: '1' });
      if (route.equipmentId) params.set('equipment', route.equipmentId);
      if (route.dailyReportId) params.set('daily', route.dailyReportId);
      if (route.hseReportId) params.set('hse', route.hseReportId);
      if (route.workOrderId) params.set('wo', route.workOrderId);
      return `${DEFECTS_PATH}?${params}`;
    }
    case 'detail':
      return `${DEFECTS_PATH}?id=${route.id}`;
  }
}

export type DefectStatusFilter = DefectStage | 'open' | 'overdue' | 'pending' | null;

export interface DefectFilter {
  search: string;
  status: DefectStatusFilter;
  priority: string | null;
  kind: string | null;
  vesselId: string | null;
  from: string | null;
  to: string | null;
}

export const NO_DEFECT_FILTER: DefectFilter = {
  search: '',
  status: null,
  priority: null,
  kind: null,
  vesselId: null,
  from: null,
  to: null,
};

/** Kapanış onayı bekliyor (yönetim ya da kaptan adımı). */
export const isPending = (item: DefectSummary) => item.closure.status === 'pending';

/** Süzgeç: arama numarada, açıklamada, ekipmanda, sistemde, konumda ve gemide. */
export function filterDefects(
  items: readonly DefectSummary[],
  filter: DefectFilter,
): DefectSummary[] {
  const words = filter.search.trim().toLocaleLowerCase().split(/\s+/).filter(Boolean);
  return items.filter((item) => {
    if (filter.vesselId && item.vesselId !== filter.vesselId) return false;
    if (filter.priority && item.priority !== filter.priority) return false;
    if (filter.kind && item.kind !== filter.kind) return false;
    if (filter.status === 'open' && item.stage === 'closed') return false;
    if (filter.status === 'overdue' && !item.overdue) return false;
    if (filter.status === 'pending' && !isPending(item)) return false;
    if (
      filter.status &&
      filter.status !== 'open' &&
      filter.status !== 'overdue' &&
      filter.status !== 'pending' &&
      item.stage !== filter.status
    ) {
      return false;
    }
    if (filter.from && item.localDate < filter.from) return false;
    if (filter.to && item.localDate > filter.to) return false;
    if (words.length === 0) return true;
    const text = [
      item.defectNo,
      item.description,
      item.equipment?.code,
      item.equipment?.name,
      item.systemName,
      item.location,
      item.vesselName,
    ]
      .filter(Boolean)
      .join(' ')
      .toLocaleLowerCase();
    return words.every((word) => text.includes(word));
  });
}

export type DefectColumn =
  | 'defectNo'
  | 'reported'
  | 'vessel'
  | 'equipment'
  | 'description'
  | 'department'
  | 'priority'
  | 'stage'
  | 'target';

const STAGE_RANK: Readonly<Record<DefectStage, number>> = {
  reported: 1,
  under_action: 2,
  verification: 3,
  closed: 4,
};
const PRIORITY_RANK: Readonly<Record<string, number>> = { low: 1, medium: 2, high: 3 };

/** Ekipmanın gösterilen adı: kod ve ad, ekipmansızsa sistem adı. */
export function equipmentLabel(item: Pick<DefectSummary, 'equipment' | 'systemName'>): string {
  if (item.equipment) {
    return [item.equipment.code, item.equipment.name].filter(Boolean).join(' · ');
  }
  return item.systemName ?? '—';
}

export const DEFECT_SORT_VALUE: Readonly<
  Record<DefectColumn, (item: DefectSummary) => string | number>
> = {
  defectNo: (item) => item.defectNo,
  reported: (item) => item.reportedAt,
  vessel: (item) => item.vesselName ?? '',
  equipment: (item) => equipmentLabel(item),
  description: (item) => item.description,
  department: (item) => item.department ?? '',
  priority: (item) => PRIORITY_RANK[item.priority ?? ''] ?? 0,
  stage: (item) => STAGE_RANK[item.stage] ?? 0,
  target: (item) => item.targetOn ?? '9999-12-31',
};

export function sortDefects(
  items: readonly DefectSummary[],
  column: DefectColumn,
  dir: 'asc' | 'desc',
): DefectSummary[] {
  const value = DEFECT_SORT_VALUE[column];
  const sign = dir === 'asc' ? 1 : -1;
  return [...items].sort((a, b) => {
    const x = value(a);
    const y = value(b);
    const order =
      typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y));
    return order * sign || b.defectNo.localeCompare(a.defectNo);
  });
}

/** Liste kartları: toplam, açık, geciken, onay bekleyen, kapanan. */
export function defectKpis(items: readonly DefectSummary[]) {
  return {
    total: items.length,
    open: items.filter((i) => i.stage !== 'closed').length,
    overdue: items.filter((i) => i.overdue).length,
    pending: items.filter(isPending).length,
    closed: items.filter((i) => i.stage === 'closed').length,
  };
}

/** Aşamanın rozet tonu (LGS-17: bildirildi kırmızı, işlemde turuncu, doğrulama mavi, kapandı yeşil). */
export function defectStageTone(
  stage: DefectStage,
): 'success' | 'warning' | 'danger' | 'info' | 'neutral' {
  switch (stage) {
    case 'closed':
      return 'success';
    case 'reported':
      return 'danger';
    case 'under_action':
      return 'warning';
    default:
      return 'info';
  }
}

export function defectPriorityTone(
  priority: string | null,
): 'danger' | 'warning' | 'success' | 'neutral' {
  switch (priority) {
    case 'high':
      return 'danger';
    case 'medium':
      return 'warning';
    case 'low':
      return 'success';
    default:
      return 'neutral';
  }
}
